/**
 * 카운터 대화 상태 (DEV_SPEC 4.1).
 *
 * - 장면·칩·입력창은 서버가 정합니다(GET /api/counter). 화면은 받은 대로 그립니다
 * - 보낸 뒤 새로 생긴 이모의 말은 한 줄씩 보여 줍니다(C-37 · D100): 첫 줄은 0.8초(답을 기다린 동안이 이미 입력 중),
 *   다음 줄은 같은 멤버면 0.8~1.2초, 다른 멤버(별채 기척 등)면 max(앞 말 읽을 시간, 올릴 말 입력 중) 1.5~4초, 모두 × 말 속도.
 *   보여 주는 동안은 칩과 입력창을 숨깁니다
 * - 보낼 때마다 clientKey 를 만들고, 실패해서 다시 보낼 때도 같은 값을 씁니다. 서버가 두 번 처리하지 않습니다
 * - 다시 열면 멈춘 장면부터 이어 갑니다(수용 기준 8). 이미 받은 말은 한꺼번에 보입니다
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { errorMessageOf, isAbortError } from '../../api/client.js';
import * as counterApi from '../../api/counter.js';
import { firstLineWaitMs, noteRevealSpeed, revealGapOf } from '../../stores/revealSpeed.js';

function newClientKey() {
    return globalThis.crypto?.randomUUID?.() ?? `c-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`;
}

/**
 * @param {{ onFinished: (response) => void, delays?: { first: number, next: number } }} options  delays 는 시험용(없으면 공식)
 *   onFinished: 서버가 첫 방문을 끝냈다고 알려 줄 때(위기 뒤 「이모랑 계속 얘기하기」 등)
 */
export function useCounter({ onFinished, delays = null }) {
    const [status, setStatus] = useState('loading');
    const [view, setView] = useState(null);
    const [hiddenIds, setHiddenIds] = useState([]);
    const [sending, setSending] = useState(false);
    // 서버가 답하기 전에 먼저 보이는 내 말(칩 글자 · 입력한 글). 내 말 → 이모 입력 중 순서 (사용자 지정 2026-10-13)
    const [pendingSpeech, setPendingSpeech] = useState(null);
    const [error, setError] = useState(null);
    const lastPayload = useRef(null);
    // 이번에 꺼낼 말 중 첫 줄인지. 첫 줄은 조금 빨리 나옵니다
    const firstLinePending = useRef(false);
    const onFinishedRef = useRef(onFinished);
    onFinishedRef.current = onFinished;

    const load = useCallback(async (signal) => {
        setStatus('loading');
        setError(null);
        try {
            const response = await counterApi.fetchCounter({ signal });
            noteRevealSpeed(response.revealSpeed);
            if (response.onboardingStep === 'done') {
                onFinishedRef.current(response);
                return;
            }
            setView(response);
            setHiddenIds([]);
            setStatus('ready');
        } catch (requestError) {
            if (isAbortError(requestError)) {
                return;
            }
            setError(errorMessageOf(requestError, '가게 문을 여는 데 시간이 걸리네요. 다시 시도해 주세요.'));
            setStatus('error');
        }
    }, []);

    useEffect(() => {
        const controller = new AbortController();
        load(controller.signal);
        return () => {
            controller.abort();
        };
    }, [load]);

    // 다음에 꺼낼 줄까지의 간격: 시험이 준 값, 아니면 공식(앞에 보인 말 → 이 줄)
    const lineDelayOf = useCallback(
        (first, nextId) => {
            if (delays) {
                return first ? delays.first : delays.next;
            }
            const list = view?.messages ?? [];
            const index = list.findIndex((message) => message.messagesId === nextId);
            // 위기 답은 기다리지 않음(31.1)
            if (['L2', 'L3'].includes(list[index]?.safetyLevel)) {
                return 0;
            }
            if (first) {
                return firstLineWaitMs();
            }
            // 카운터 말의 주인: 장면 줄은 그 멤버(별채 기척 등), 나머지는 이모
            const asLine = (message) =>
                message && {
                    body: message.body,
                    memberKey: message.scene?.memberKey ?? (message.role === 'user' ? 'user' : 'imo'),
                };
            return revealGapOf(asLine(list[index - 1]), asLine(list[index]));
        },
        [delays, view]
    );

    // 새로 생긴 말을 한 줄씩 꺼냅니다. 유저 자신의 말은 바로 보입니다(hiddenIds 에 넣지 않습니다)
    useEffect(() => {
        if (!hiddenIds.length) {
            return undefined;
        }
        const delay = lineDelayOf(firstLinePending.current, hiddenIds[0]);
        const timer = setTimeout(() => {
            firstLinePending.current = false;
            setHiddenIds((previous) => previous.slice(1));
        }, delay);
        return () => {
            clearTimeout(timer);
        };
    }, [hiddenIds, lineDelayOf]);

    const send = useCallback(
        async (input, { retry = false } = {}) => {
            if (sending) {
                return;
            }
            const payload =
                retry && lastPayload.current ? lastPayload.current : { ...input, clientKey: newClientKey() };
            lastPayload.current = payload;
            const chipLabel = view?.controls?.chips?.find((chip) => chip.id === payload.chipId)?.label ?? null;
            setPendingSpeech(payload.body ?? payload.post ?? chipLabel);
            setSending(true);
            setError(null);
            try {
                const response = await counterApi.sendCounterInput(payload);
                noteRevealSpeed(response.revealSpeed);
                lastPayload.current = null;
                if (response.redirect) {
                    onFinishedRef.current(response);
                    return;
                }
                const userMessageIds = new Set(
                    response.messages.filter((message) => message.role === 'user').map((message) => message.messagesId)
                );
                firstLinePending.current = true;
                setView(response);
                setHiddenIds((response.appendedIds ?? []).filter((id) => !userMessageIds.has(id)));
                return response;
            } catch (requestError) {
                setError(errorMessageOf(requestError, '이모가 잘 못 들었대요. 다시 보내 주세요.'));
            } finally {
                setPendingSpeech(null);
                setSending(false);
            }
        },
        [sending, view]
    );

    const retry = useCallback(() => send(null, { retry: true }), [send]);

    const revealing = hiddenIds.length > 0;
    const hidden = new Set(hiddenIds);
    const messages = view ? view.messages.filter((message) => !hidden.has(message.messagesId)) : [];

    return {
        status,
        view,
        messages,
        revealing,
        // 이모가 말하는 중: 보낸 답을 기다리거나, 받은 말을 한 줄씩 보여 주는 동안
        imoTyping: sending || revealing,
        pendingSpeech,
        sending,
        error,
        canRetry: Boolean(lastPayload.current) && !sending,
        send,
        retry,
        reload: () => load(),
        setView,
    };
}
